import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from '../hooks/use-toast';
import {
  Check, Sparkles, Video, BookOpen, 
  BarChart3, GraduationCap, Loader2,
  CreditCard, Crown, Zap, Coins, Gift,
  MessageSquare, Briefcase, Target
} from 'lucide-react';
import { AI_PERSONAS } from '../constants/aiPersonas';

const Pricing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingPackage, setLoadingPackage] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [packages, setPackages] = useState(null);

  useEffect(() => {
    fetchPackages();
    if (user) {
      fetchUsageStatus();
    }
  }, [user]);

  const fetchPackages = async () => {
    try {
      const response = await api.get('/subscription/packages');
      setPackages(response.data);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  };

  const fetchUsageStatus = async () => {
    try {
      const response = await api.get('/subscription/usage');
      setUsageData(response.data);
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    }
  };

  const handlePurchase = async (packageId) => {
    if (!user) {
      navigate('/login?redirect=/pricing');
      return;
    }

    setLoading(true);
    setLoadingPackage(packageId);

    try {
      const response = await api.post(`/subscription/create-checkout/credits/${packageId}`, {}, {
        headers: { 'Origin': window.location.origin }
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to start checkout", variant: "destructive" });
    } finally {
      setLoading(false);
      setLoadingPackage(null);
    }
  };

  const featureCosts = packages?.feature_costs || {
    interview: 25,
    admission_chances: 2,
    career_simulator: 2,
    academic_advisor: 2,
    essay_review: 3
  };

  const creditPackages = packages?.packages || [
    { id: 'starter', name: 'Starter Pack', amount: 10, credits: 12 },
    { id: 'popular', name: 'Popular Pack', amount: 20, credits: 25, badge: 'Best Value', savings: 'Save 17%' },
    { id: 'pro', name: 'Pro Pack', amount: 40, credits: 55, badge: 'Most Credits', savings: 'Save 27%' }
  ];

  const features = [
    { name: 'ALEC - AI Visa Officer', cost: featureCosts.interview, icon: <Video className="h-5 w-5" />, desc: '4-minute mock interview', avatar: AI_PERSONAS.ALEC.avatar },
    { name: 'WONG - Admission Predictor', cost: featureCosts.admission_chances, icon: <Target className="h-5 w-5" />, desc: 'College acceptance prediction', avatar: AI_PERSONAS.WONG.avatar },
    { name: 'Ashish - Career Simulator', cost: featureCosts.career_simulator, icon: <Briefcase className="h-5 w-5" />, desc: 'Career path visualization', avatar: AI_PERSONAS.ASHISH.avatar },
    { name: 'Faith - Academic Advisor', cost: featureCosts.academic_advisor, icon: <GraduationCap className="h-5 w-5" />, desc: 'Major selection guidance', avatar: AI_PERSONAS.FAITH.avatar },
    { name: 'Wasif - Essay Assistant', cost: featureCosts.essay_review, icon: <MessageSquare className="h-5 w-5" />, desc: 'Essay review + AI detection', avatar: AI_PERSONAS.WASIF.avatar }
  ];

  const freeFeatures = [
    'F1 Visa Guide with AI Chat',
    'Why College Matters Guide',
    'Paying for College Guide'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-16 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-sm px-4 py-1 border-0">
            <Coins className="h-3 w-3 mr-1 inline" /> Credit-Based System
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Buy Credits, Use Anywhere
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Purchase credits once and use them across all AI features. No subscriptions, no expiration.
          </p>
        </div>

        {/* Current Balance */}
        {user && usageData && (
          <div className="max-w-md mx-auto mb-12">
            <Card className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/30 backdrop-blur-lg">
              <CardContent className="py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                      <Coins className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-amber-200">Your Balance</p>
                      <p className="text-3xl font-bold text-white">{usageData.credits || 0}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-amber-200 text-sm">credits</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Credit Packages */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-16">
          {creditPackages.map((pkg) => (
            <Card 
              key={pkg.id}
              className={`relative border-2 transition-all hover:scale-105 ${
                pkg.badge === 'Best Value' 
                  ? 'border-amber-500 bg-gradient-to-b from-amber-500/10 to-transparent' 
                  : 'border-white/20 bg-white/5 hover:border-white/40'
              } backdrop-blur-lg`}
              data-testid={`package-${pkg.id}`}
            >
              {pkg.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className={`px-3 py-1 ${
                    pkg.badge === 'Best Value' 
                      ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white' 
                      : 'bg-purple-500 text-white'
                  }`}>
                    {pkg.badge}
                  </Badge>
                </div>
              )}
              <CardHeader className="text-center pt-8">
                <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
                  pkg.badge === 'Best Value'
                    ? 'bg-gradient-to-br from-amber-400 to-orange-500'
                    : 'bg-gradient-to-br from-purple-500 to-pink-500'
                }`}>
                  <Coins className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-2xl text-white">{pkg.name}</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-white">${pkg.amount}</span>
                </div>
                <p className="text-3xl font-bold text-amber-400 mt-2">{pkg.credits} Credits</p>
                {pkg.savings && (
                  <Badge className="mt-2 bg-green-500/20 text-green-400 border-green-500/30">
                    {pkg.savings}
                  </Badge>
                )}
              </CardHeader>
              <CardContent>
                <div className="text-center text-gray-400 text-sm mb-4">
                  ${(pkg.amount / pkg.credits).toFixed(2)} per credit
                </div>
                <Button 
                  className={`w-full ${
                    pkg.badge === 'Best Value'
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600'
                      : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'
                  }`}
                  onClick={() => handlePurchase(pkg.id)}
                  disabled={loading}
                  data-testid={`buy-${pkg.id}`}
                >
                  {loading && loadingPackage === pkg.id ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
                  ) : (
                    <>Buy {pkg.credits} Credits</>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Feature Costs */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Credit Costs Per Feature</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((feature) => (
              <Card key={feature.name} className="bg-white/5 border-white/10 backdrop-blur-lg hover:bg-white/10 transition-colors">
                <CardContent className="py-4 flex items-center gap-4">
                  {feature.avatar ? (
                    <img 
                      src={feature.avatar} 
                      alt={feature.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-emerald-500/50"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center text-white">
                      {feature.icon}
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="text-white font-medium">{feature.name}</p>
                    <p className="text-gray-400 text-xs">{feature.desc}</p>
                  </div>
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                    {feature.cost} credits
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Free Features */}
        <div className="max-w-2xl mx-auto mb-16">
          <Card className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/30 backdrop-blur-lg">
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mb-4">
                <Gift className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-2xl text-white">Free Guides</CardTitle>
              <p className="text-gray-300">These guides are always available, no credits needed</p>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {freeFeatures.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-gray-200">
                    <Check className="h-5 w-5 text-emerald-400" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <Card className="bg-white/5 border-white/10 backdrop-blur-lg">
              <CardContent className="py-4">
                <h3 className="font-semibold text-white mb-2">Do credits expire?</h3>
                <p className="text-gray-300">
                  No! Your credits never expire. Buy once and use them whenever you need.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10 backdrop-blur-lg">
              <CardContent className="py-4">
                <h3 className="font-semibold text-white mb-2">Can I use credits for any feature?</h3>
                <p className="text-gray-300">
                  Yes! Credits work across all AI features - ALEC (Interview), WONG (Admission Chances), Ashish (Career Simulator), Faith (Academic Advisor), and Essay Review.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border-white/10 backdrop-blur-lg">
              <CardContent className="py-4">
                <h3 className="font-semibold text-white mb-2">Which package should I choose?</h3>
                <p className="text-gray-300">
                  The Popular Pack ($20 for 25 credits) covers one full ALEC video interview session. 
                  The Pro Pack ($40 for 55 credits) gives you 2 interview sessions plus several other AI features.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CTA */}
        {!user && (
          <div className="text-center mt-16">
            <p className="text-gray-300 mb-4">Create an account to get started</p>
            <Button 
              className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
              onClick={() => navigate('/signup')}
            >
              Sign Up Now
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Pricing;
